import { httpClient } from '@/utils/httpClient';
import { API_CONFIG } from '@/config/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ShelfMapLayout {
  id: string;
  name: string;
  description: string | null;
  warehouse_entity_id: string;
  warehouse_name: string | null;
  warehouse_identifier: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ShelfMapSlot {
  id: string;
  layout_id: string;
  aisle_label: string;
  shelf_number: number;
  column_number: number;
  location_entity_id: string | null;
  location_name: string | null;
  location_breadcrumb: string | null;
  label_override: string | null;
  created_at: string;
  updated_at: string;
}

export interface ShelfMapLayoutWithSlots extends ShelfMapLayout {
  slots: ShelfMapSlot[];
}

export interface ShelfMapInventoryItem {
  catalog_id: string;
  lin: string;
  nsn: string;
  nomenclature: string;
  size?: string;
  quantity: number;
}

export interface ShelfMapLocationInventory {
  location_id: string;
  location_name: string | null;
  items: ShelfMapInventoryItem[];
}

export interface ShelfMapLocationInventoryPresence {
  location_id: string;
  has_inventory: boolean;
  total_stock_quantity: number;
}

export interface ShelfMapInventorySearchMatch {
  location_entity_id: string;
  lin: string;
  nomenclature: string;
}

export interface ShelfMapInventorySearchResponse {
  layout_id: string;
  query: string;
  matches: ShelfMapInventorySearchMatch[];
}

export interface ShelfMapContextSearchMatch {
  location_entity_id: string;
  lin: string;
}

export interface ShelfMapContextSearchResponse {
  layout_id: string;
  matches: ShelfMapContextSearchMatch[];
  /** LINs whose lookup chunk failed upstream; not silently dropped. */
  failed_lins: string[];
}

export interface ShelfMapBulkInventoryPresenceResponse {
  layout_id: string;
  locations: ShelfMapLocationInventoryPresence[];
}

// ---------------------------------------------------------------------------
// Composition types (Issue #224)
// ---------------------------------------------------------------------------

export interface ShelfMapCompositionLocationType {
  id: string;
  type_name: string;
  structural_type: string;
  structural_level: number | null;
  is_stockable: boolean | null;
}

export interface ShelfMapCompositionLocation {
  id: string;
  display_name: string;
  location_type_id: string | null;
  parent_location_id: string | null;
  breadcrumb: string | null;
  is_stockable: boolean | null;
}

/**
 * Full template+location composition payload for a shelf-map layout.
 * Canonical path: layout → warehouse → template → location_types → locations.
 * Arrays are always present (never null); empty when no template binding exists.
 */
export interface ShelfMapCompositionPayload {
  layout_id: string;
  template_id: string | null;
  template_name: string | null;
  location_types: ShelfMapCompositionLocationType[];
  locations: ShelfMapCompositionLocation[];
}

export interface CreateShelfMapLayoutInput {
  name: string;
  description?: string | null;
  warehouse_entity_id: string;
  is_active?: boolean;
}

export interface UpdateShelfMapLayoutInput {
  name?: string;
  description?: string | null;
  is_active?: boolean;
}

export interface CreateShelfMapSlotInput {
  aisle_label: string;
  shelf_number: number;
  column_number: number;
  location_entity_id?: string | null;
  label_override?: string | null;
}

export interface BatchCreateShelfMapSlotInput {
  aisle_label: string;
  shelf_number: number;
  column_number: number;
  location_entity_id?: string | null;
  label_override?: string | null;
}

export interface BatchCreateShelfMapSlotsResult {
  created: number;
  skipped: number;
  slots: ShelfMapSlot[];
}

export interface UpdateShelfMapSlotInput {
  location_entity_id?: string | null;
  label_override?: string | null;
  /** Move slot to a different aisle row. */
  aisle_label?: string;
  /** Move slot to a different shelf sub-row. */
  shelf_number?: number;
  /** Move slot to a different column position. */
  column_number?: number;
}

export interface MoveShelfMapSlotInput {
  /** Target aisle label to move the slot into. */
  target_aisle_label: string;
  /** Target shelf number within the target aisle. */
  target_shelf_number: number;
}

// ---------------------------------------------------------------------------
// Eligibility types (Issue #227)
// ---------------------------------------------------------------------------

/**
 * Reason codes for map launch ineligibility.
 * Used by the UI to surface user-friendly messages without hard errors.
 */
export type ShelfMapIneligibilityReason =
  | 'no-primary-secondary'
  | 'no-template-binding'
  | 'no-map-config'
  | 'no-slot-match'
  | 'warehouse-mismatch';

/** Response from POST /api/rest/app/v1/shelf-map/eligibility. */
export interface ShelfMapEligibilityResult {
  eligible: boolean;
  reason?: ShelfMapIneligibilityReason;
  /** User-friendly message from the server; display directly to the user. */
  message?: string;
}

export interface ShelfMapEligibilityInput {
  warehouse_entity_id: string;
  primary_location_id?: string | null;
  secondary_location_id?: string | null;
  /**
   * Set by general/site-wide map viewers (e.g. Front Window) that are not
   * scoped to a single catalog item's primary/secondary location. Skips the
   * item-specific eligibility checks on the backend.
   */
  warehouse_only?: boolean;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

const BASE = API_CONFIG.ENDPOINTS.SHELF_MAP;

export const shelfMapService = {
  /** List all layouts, optionally filtered by warehouse entity ID. */
  async listLayouts(warehouseEntityId?: string): Promise<ShelfMapLayout[]> {
    const params = warehouseEntityId
      ? `?warehouse_entity_id=${encodeURIComponent(warehouseEntityId)}`
      : '';
    return httpClient.get<ShelfMapLayout[]>(`${BASE}${params}`);
  },

  /** Create a new shelf map layout. */
  async createLayout(input: CreateShelfMapLayoutInput): Promise<ShelfMapLayout> {
    return httpClient.post<ShelfMapLayout>(BASE, input);
  },

  /** Get a layout with all its slots. */
  async getLayout(layoutId: string): Promise<ShelfMapLayoutWithSlots> {
    return httpClient.get<ShelfMapLayoutWithSlots>(`${BASE}/${layoutId}`);
  },

  /** Update a layout's metadata. */
  async updateLayout(layoutId: string, input: UpdateShelfMapLayoutInput): Promise<ShelfMapLayout> {
    return httpClient.patch<ShelfMapLayout>(`${BASE}/${layoutId}`, input);
  },

  /** Delete a layout (cascades slots). */
  async deleteLayout(layoutId: string): Promise<void> {
    return httpClient.delete(`${BASE}/${layoutId}`);
  },

  /** Add a slot to a layout. */
  async createSlot(layoutId: string, input: CreateShelfMapSlotInput): Promise<ShelfMapSlot> {
    return httpClient.post<ShelfMapSlot>(`${BASE}/${layoutId}/slot`, input);
  },

  /**
   * Batch-create slots in a single request.
   * Duplicate coordinates are silently skipped (server uses ON CONFLICT DO NOTHING).
   * Accepts up to 500 slots per call; caller should chunk larger sets.
   */
  async batchCreateSlots(
    layoutId: string,
    slots: BatchCreateShelfMapSlotInput[],
  ): Promise<BatchCreateShelfMapSlotsResult> {
    return httpClient.post<BatchCreateShelfMapSlotsResult>(`${BASE}/${layoutId}/slots/batch`, {
      slots,
    });
  },

  /** Update a slot's linked location or label override. */
  async updateSlot(
    layoutId: string,
    slotId: string,
    input: UpdateShelfMapSlotInput,
  ): Promise<ShelfMapSlot> {
    return httpClient.patch<ShelfMapSlot>(`${BASE}/${layoutId}/slot/${slotId}`, input);
  },

  /** Move a slot to a different aisle/shelf row (server computes next free column). */
  async moveSlot(
    layoutId: string,
    slotId: string,
    input: MoveShelfMapSlotInput,
  ): Promise<ShelfMapSlot> {
    return httpClient.post<ShelfMapSlot>(`${BASE}/${layoutId}/slot/${slotId}/move`, input);
  },

  /** Remove a slot from a layout. */
  async deleteSlot(layoutId: string, slotId: string): Promise<void> {
    return httpClient.delete(`${BASE}/${layoutId}/slot/${slotId}`);
  },

  /** Get on-hand inventory for a selected location within a layout. */
  async getLocationInventory(
    layoutId: string,
    locationId: string,
  ): Promise<ShelfMapLocationInventory> {
    return httpClient.get<ShelfMapLocationInventory>(
      `${BASE}/${layoutId}/location/${locationId}/inventory`,
    );
  },

  /** Get inventory presence for multiple locations in one request. */
  async getLocationInventoryPresenceBulk(
    layoutId: string,
    locationIds: string[],
  ): Promise<ShelfMapBulkInventoryPresenceResponse> {
    return httpClient.post<ShelfMapBulkInventoryPresenceResponse>(
      `${BASE}/${layoutId}/inventory/bulk`,
      { location_ids: locationIds },
    );
  },

  /** Search all slots in a layout for inventory matching a LIN or nomenclature query. */
  async searchInventory(layoutId: string, query: string): Promise<ShelfMapInventorySearchResponse> {
    return httpClient.post<ShelfMapInventorySearchResponse>(
      `${BASE}/${layoutId}/inventory/search`,
      { query },
    );
  },

  /**
   * Bulk, exact-match LIN lookup used to scope the map to a hand receipt's
   * catalog LINs in bounded, chunked requests rather than one request per
   * LIN. Failed LIN chunks are reported in `failed_lins` (#1130).
   */
  async searchInventoryByLins(
    layoutId: string,
    lins: string[],
  ): Promise<ShelfMapContextSearchResponse> {
    return httpClient.post<ShelfMapContextSearchResponse>(
      `${BASE}/${layoutId}/inventory/context-search`,
      { lins },
    );
  },

  /**
   * Check whether a given item + warehouse context is eligible to launch a shelf map.
   * Always resolves (never throws for ineligible cases); only throws on network/auth errors.
   * Issue: #227
   */
  async checkEligibility(input: ShelfMapEligibilityInput): Promise<ShelfMapEligibilityResult> {
    return httpClient.post<ShelfMapEligibilityResult>(`${BASE}/eligibility`, input);
  },

  /**
   * Get the full template+location composition for a layout.
   * Composition path: layout → warehouse → template → location_types → locations.
   * Returns empty arrays when no template binding exists (never throws for missing template).
   * Throws 404 when the layout itself does not exist.
   * Issue: #224
   */
  async getComposition(layoutId: string): Promise<ShelfMapCompositionPayload> {
    return httpClient.get<ShelfMapCompositionPayload>(`${BASE}/${layoutId}/composition`);
  },
};
